import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { useBooking } from "@/context/BookingContext";
import { trackEvent } from "@/utils/analytics";
import "./HeroSection.scss";

const scrollOptions = { duration: 500, smooth: true, offset: -80 };

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const { openBooking } = useBooking();
  const accentWords = t("hero.accentWords", { returnObjects: true }) as string[];

  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"in" | "show" | "out">("show");
  const orbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout>;
    let outTimer: ReturnType<typeof setTimeout>;
    let innerTimer: ReturnType<typeof setTimeout>;
    const loop = () => {
      setPhase("show");
      showTimer = setTimeout(() => {
        setPhase("out");
        outTimer = setTimeout(() => {
          setIdx((i) => (i + 1) % accentWords.length);
          setPhase("in");
          innerTimer = setTimeout(() => setPhase("show"), 50);
        }, 550);
      }, 2200);
    };
    loop();
    const interval = setInterval(loop, 2800);
    return () => { clearInterval(interval); clearTimeout(showTimer); clearTimeout(outTimer); clearTimeout(innerTimer); };
  }, [accentWords.length]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!orbRef.current) return;
      const rect = orbRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      orbRef.current.style.setProperty("--px", `${dx * 14}px`);
      orbRef.current.style.setProperty("--py", `${dy * 14}px`);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  const word = accentWords[idx] ?? "";

  return (
    <section id="hero-section" className="hero-section">
      <div className="container hero-grid">
        <div className="hero-copy">
          <span className="eyebrow">
            <span className="eyebrow-pulse" aria-hidden="true" />
            {t("hero.badge")}
          </span>
          <h1 className="hero-h1">
            {t("hero.titleLine1")}
            <br />
            <span className="rotator" aria-live="polite">
              <span className="rotator-slot">
                <span className={`rotator-word${phase === "out" ? " out" : phase === "in" ? " in" : ""}`}>
                  {word}
                </span>
              </span>
            </span>
          </h1>
          <p className="hero-sub">{t("hero.subtitle")}</p>
          <div className="hero-ctas">
            <Link to="portfolio-section" {...scrollOptions} className="btn btn-primary" onClick={() => trackEvent("cta_click", { location: "hero_primary" })}>
              {t("hero.seeWork")} <ArrowIcon />
            </Link>
            <button className="btn btn-ghost" onClick={() => { trackEvent("cta_click", { location: "hero_secondary" }); openBooking("hero"); }}>
              {t("booking.cta")}
            </button>
          </div>
          <div className="hero-meta">
            <span><span className="meta-tick" aria-hidden="true">✦</span> {t("hero.meta.production")}</span>
            <span><span className="meta-tick" aria-hidden="true">✦</span> {t("hero.meta.engineered")}</span>
            <span><span className="meta-tick" aria-hidden="true">✦</span> {t("hero.meta.global")}</span>
          </div>
        </div>

        <div
          className="hero-visual"
          ref={orbRef}
          aria-hidden="true"
          style={{ transform: "translate(var(--px, 0), var(--py, 0))", transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
        >
          <div className="hv-stage">
            <svg className="hv-spine" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M 50 14 L 50 50" />
              <path d="M 50 50 L 50 86" />
            </svg>

            <div className="hv-card hv-code">
              <div className="hv-card-bar">
                <span className="hv-dot" /><span className="hv-dot" /><span className="hv-dot" />
                <span className="hv-card-title">model.ts</span>
                <span className="hv-card-tag">TypeScript</span>
              </div>
              <pre className="hv-code-body"><span className="cl"><span className="kw">import</span> {"{ Model }"} <span className="kw">from</span> <span className="st">&quot;@devbloom/sdk&quot;</span></span>
<span className="cl"><span className="cm">{"// "}{t("hero.visual.codeComment")}</span></span>
<span className="cl"><span className="kw">const</span> m = <span className="kw">new</span> Model(<span className="st">&quot;forecast-v3&quot;</span>)</span>
<span className="cl"><span className="kw">await</span> m.train({"{ data, epochs: "}<span className="nm">12</span>{" }"})</span>
<span className="cl"><span className="hv-cursor"><span className="kw">await</span> m.deploy()</span></span></pre>
            </div>

            <div className="hv-card hv-model">
              <div className="hv-model-head">
                <div className="hv-model-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <circle cx="6" cy="6" r="2" /><circle cx="18" cy="6" r="2" />
                    <circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" />
                    <circle cx="12" cy="12" r="2.5" />
                    <path d="M8 6h8M6 8v8M18 8v8M8 18h8M8 7l3 4M16 7l-3 4M8 17l3-4M16 17l-3-4" />
                  </svg>
                </div>
                <div>
                  <div className="hv-model-name">forecast-v3</div>
                  <div className="hv-model-meta">{t("hero.visual.modelMeta")}</div>
                </div>
                <span className="hv-model-pulse"><span className="hv-pulse-dot" /> {t("hero.visual.active")}</span>
              </div>
              <div className="hv-bars">
                {[62, 38, 84, 51, 73, 29, 90, 47, 66, 34, 78, 55].map((v, i) => (
                  <span key={i} className="hv-bar" style={{ height: `${v}%`, animationDelay: `${i * 0.12}s` }} />
                ))}
              </div>
              <div className="hv-model-stats">
                <div><span className="hv-stat-num">99.4<span className="hv-stat-unit">%</span></span><span className="hv-stat-lbl">{t("hero.visual.accuracy")}</span></div>
                <div className="hv-stat-divider" />
                <div><span className="hv-stat-num">12<span className="hv-stat-unit">ms</span></span><span className="hv-stat-lbl">{t("hero.visual.latency")}</span></div>
                <div className="hv-stat-divider" />
                <div><span className="hv-stat-num">2.4<span className="hv-stat-unit">M</span></span><span className="hv-stat-lbl">{t("hero.visual.requests")}</span></div>
              </div>
            </div>

            <div className="hv-card hv-product">
              <div className="hv-product-row">
                <div className="hv-product-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </div>
                <div className="hv-product-text">
                  <div className="hv-product-title">{t("hero.visual.productTitle")}</div>
                  <div className="hv-product-sub">{t("hero.visual.productSub")}</div>
                </div>
                <span className="hv-product-time">{t("hero.visual.justNow")}</span>
              </div>
            </div>
          </div>

          <div className="hv-label hv-label-1"><span className="hv-label-num">01</span><span>{t("hero.visual.stepEngineer")}</span></div>
          <div className="hv-label hv-label-2"><span className="hv-label-num">02</span><span>{t("hero.visual.stepDeploy")}</span></div>
          <div className="hv-label hv-label-3"><span className="hv-label-num">03</span><span>{t("hero.visual.stepDecision")}</span></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
